import { apiClient } from '../api/client';

export type TicketStatus = 'OPEN' | 'IN_PROGRESS' | 'AWAITING_CUSTOMER' | 'RESOLVED' | 'CLOSED';
export type TicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface TicketCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface Ticket {
  id: string;
  playerId: string;
  categoryId: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  assignedToId?: string;
  isAssigned: boolean;
  isClosed: boolean;
  closedAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface TicketMessage {
  id: string;
  message: string;
  isStaff: boolean;
  authorId: string;
  authorName: string;
  authorNick?: string; // only for player messages
  createdAt: string;
}

export interface TicketWithMessages {
  ticket: Ticket;
  messages: TicketMessage[];
}

export interface StaffTicket extends Ticket {
  claimedByPlayerId?: string;
  isClaimedByMe: boolean;
  isClaimedByOther: boolean;
}

export interface StaffTicketWithMessages {
  ticket: StaffTicket;
  messages: TicketMessage[];
  currentPlayerId: string;
  permissions: {
    canReply: boolean;
    canClaim: boolean;
    canRelease: boolean;
    canClose: boolean;
    canViewAll: boolean;
  };
}

export interface StaffTicketListResponse {
  tickets: StaffTicket[];
  pagination: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
  canViewAll: boolean;
  currentPlayerId: string;
}

export interface CreateTicketRequest {
  categoryId: string;
  title: string;
  message: string;
  priority: TicketPriority;
}

export interface ReplyToTicketRequest {
  message: string;
}

export const ticketService = {
  /**
   * Lista todas as categorias ativas de tickets
   */
  async listActiveCategories(storeId: string): Promise<TicketCategory[]> {
    const response = await apiClient.get<{ categories: TicketCategory[] }>(
      `/public/stores/${storeId}/ticket-categories`
    );
    return response.categories || [];
  },

  /**
   * Cria um novo ticket
   */
  async createTicket(storeId: string, data: CreateTicketRequest): Promise<{ ticket: Ticket }> {
    const response = await apiClient.post<{ ticket: Ticket }>(
      `/public/stores/${storeId}/tickets`,
      data
    );
    return response;
  },

  /**
   * Lista todos os tickets do jogador
   */
  async listPlayerTickets(storeId: string): Promise<Ticket[]> {
    const response = await apiClient.get<{ tickets: Ticket[] }>(
      `/public/stores/${storeId}/tickets`
    );
    return response.tickets || [];
  },

  /**
   * Obtém detalhes de um ticket específico
   */
  async getTicketDetails(storeId: string, ticketId: string): Promise<TicketWithMessages> {
    const response = await apiClient.get<TicketWithMessages>(
      `/public/stores/${storeId}/tickets/${ticketId}`
    );
    return response;
  },

  /**
   * Adiciona uma resposta a um ticket
   */
  async replyToTicket(
    storeId: string,
    ticketId: string,
    data: ReplyToTicketRequest
  ): Promise<{ message: TicketMessage }> {
    const response = await apiClient.post<{ message: TicketMessage }>(
      `/public/stores/${storeId}/tickets/${ticketId}/reply`,
      data
    );
    return response;
  },

  // Staff-player endpoints
  async staffListTickets(
    storeId: string,
    filters?: { status?: TicketStatus; assignedToMe?: boolean; page?: number; perPage?: number }
  ): Promise<StaffTicketListResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.assignedToMe !== undefined) params.append('assignedToMe', String(filters.assignedToMe));
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.perPage) params.append('perPage', String(filters.perPage));
    const qs = params.toString();
    const url = `/public/stores/${storeId}/staff/tickets${qs ? `?${qs}` : ''}`;
    return apiClient.get<StaffTicketListResponse>(url);
  },

  async staffGetTicketDetails(storeId: string, ticketId: string): Promise<StaffTicketWithMessages> {
    return apiClient.get<StaffTicketWithMessages>(
      `/public/stores/${storeId}/staff/tickets/${ticketId}`
    );
  },

  async staffClaimTicket(storeId: string, ticketId: string): Promise<{ ticket: Ticket }> {
    return apiClient.put<{ ticket: Ticket }>(
      `/public/stores/${storeId}/staff/tickets/${ticketId}/claim`,
      {}
    );
  },

  async staffReleaseTicket(storeId: string, ticketId: string): Promise<{ ticket: Ticket }> {
    return apiClient.put<{ ticket: Ticket }>(
      `/public/stores/${storeId}/staff/tickets/${ticketId}/release`,
      {}
    );
  },

  async staffUpdateStatus(storeId: string, ticketId: string, status: TicketStatus): Promise<{ ticket: Ticket }> {
    return apiClient.patch<{ ticket: Ticket }>(
      `/public/stores/${storeId}/staff/tickets/${ticketId}/status`,
      { status }
    );
  },

  async staffReplyToTicket(
    storeId: string,
    ticketId: string,
    data: ReplyToTicketRequest
  ): Promise<{ message: TicketMessage }> {
    return apiClient.post<{ message: TicketMessage }>(
      `/public/stores/${storeId}/staff/tickets/${ticketId}/reply`,
      data
    );
  },
};
